import {
  Button,
  Card,
  Form,
  message,
  Select,
  Space,
  Spin,
  Transfer,
  Typography
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../../constants/permissions";
import { sellerApi } from "../../../services/api";
import { SellerBasicInfo } from "../../../types/seller.types";
import type { TransferItem as AntdTransferItem, TransferDirection } from 'antd/es/transfer';

const { Title } = Typography;

interface TransferItem extends AntdTransferItem {
  key: string;
  title: string;
  description: string;
}

const ManageReferencesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sellers, setSellers] = useState<SellerBasicInfo[]>([]);
  const [referenceOptions, setReferenceOptions] = useState<SellerBasicInfo[]>([]);
  const [transferItems, setTransferItems] = useState<TransferItem[]>([]);
  const [targetKeys, setTargetKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await sellerApi.getSellerNames();
        if (response.status === "SUCCESS") {
          setSellers(response.data);
          setReferenceOptions(response.data);

          const items: TransferItem[] = response.data.map((seller: SellerBasicInfo) => ({
            key: seller.id.toString(),
            title: seller.name,
            description: seller.phone,
            disabled: false
          }));
          setTransferItems(items);
        } else {
          message.error("Failed to fetch sellers");
        }
      } catch (error) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : "Failed to fetch sellers";
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleReferenceChange = (referenceId: string) => {
    setTargetKeys([]);
    form.setFieldsValue({ selectedSellers: [] });
  };

  const filterOption = (inputValue: string, item: TransferItem) => {
    return item.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1 || 
           item.description.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
  };

  const handleSelectChange = (sourceSelectedKeys: React.Key[], targetSelectedKeys: React.Key[]) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const handleChange = (nextTargetKeys: React.Key[], direction: TransferDirection, moveKeys: React.Key[]) => {
    setTargetKeys(nextTargetKeys);
    form.setFieldsValue({ selectedSellers: nextTargetKeys });
  };

  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      
      if (!values.referenceId) {
        message.error("Please select a reference");
        return;
      }
      
      if (!values.selectedSellers || values.selectedSellers.length === 0) {
        message.error("Please select at least one seller to update");
        return;
      }
      
      const data = {
        referenceId: values.referenceId,
        sellerIds: values.selectedSellers
      };
      
      const response = await sellerApi.updateReferences(data);
      
      if (response.status === "SUCCESS") {
        message.success("References updated successfully");
        setTargetKeys([]);
        form.resetFields();
      } else {
        message.error(response.message || "Failed to update references");
      }
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Failed to update references";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      title="Manage Seller References"
      style={{ maxWidth: 1000, margin: "0 auto" }}
      extra={
        <Button onClick={() => navigate(PERMISSIONS.LIST_SELLERS.route)}>
          Back to List
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="referenceId"
            label="Select Reference"
            rules={[{ required: true, message: "Please select a reference" }]}
          >
            <Select
              showSearch
              placeholder="Search by name or phone"
              optionFilterProp="children"
              onChange={handleReferenceChange}
              filterOption={(input, option) => 
                (option?.label?.toString().toLowerCase() || '').includes(input.toLowerCase())
              }
              options={referenceOptions.map(seller => ({
                value: seller.id.toString(),
                label: `${seller.name} (${seller.phone})`
              }))}
            />
          </Form.Item>

          <Form.Item
            name="selectedSellers"
            label="Select Sellers to Update"
            rules={[
              { 
                required: true, 
                message: "Please select at least one seller" 
              }
            ]}
          >
            <Transfer
              dataSource={transferItems}
              showSearch
              filterOption={filterOption}
              targetKeys={targetKeys}
              selectedKeys={selectedKeys}
              onChange={handleChange}
              onSelectChange={handleSelectChange}
              render={item => (
                <div>
                  <div>{item.title}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{item.description}</div>
                </div>
              )}
              listStyle={{
                width: 450,
                height: 500,
              }}
              operations={['Select', 'Deselect']}
              titles={['Available Sellers', 'Selected Sellers']}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                Update References
              </Button>
              <Button 
                onClick={() => {
                  form.resetFields();
                  setTargetKeys([]);
                }}
              >
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default ManageReferencesPage; 